import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'
import { useMinWidth } from '@/lib/useMediaQuery'

export function Page({
  fill = false,
  action,
  children,
}: {
  /** Fills the viewport instead of growing past it, so the title, actions and
   *  filters do not slide away when a list is longer than the window. Desktop
   *  only: on a phone the whole page scrolling is the right behaviour. */
  fill?: boolean
  /**
   * The one action the screen exists for, repeated into the thumb arc.
   *
   * Pass the same node to `PageHeader`'s `primary`. Exactly one of the two
   * renders it — the header from `lg` up, this bar below — because on a phone
   * a button at the top of a list is a button you scroll away from, and the
   * rows of filters above it had already pushed the first record off screen.
   *
   * One of the two, not both hidden by a class: two buttons in the document
   * answering to the same name is two things for a screen reader to offer and
   * two for a test to have to choose between.
   */
  action?: ReactNode
  children: ReactNode
}) {
  const wide = useMinWidth('lg')

  return (
    <>
      <main
        className={cx(
          // Kept identical to the shell's own gutter, or one screen starts 16px
          // further right than the one beside it.
          'w-full max-w-wide px-4 pt-4 pb-6 lg:px-6 lg:pt-6 lg:pb-8',
          fill && 'lg:flex lg:h-full lg:flex-col lg:overflow-hidden lg:pb-6',
        )}
      >
        {children}
      </main>

      {action && !wide && (
        // Sticky rather than fixed: it sits at the end of the document, so it
        // never covers the last row of a list the way a floating button does.
        <div className="sticky bottom-0 z-sticky border-t border-line bg-surface px-4 pt-3 pb-safe-3">
          <div className="mx-auto w-full max-w-[60rem] [&>*]:w-full">{action}</div>
        </div>
      )}
    </>
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  primary,
  children,
}: {
  /** Which part of the product this is — matches the sidebar grouping. */
  eyebrow?: string
  title: string
  description?: string
  actions?: ReactNode
  /** The screen's own action. Shown here from `lg` up; on a phone it lives in
   *  `Page`'s bottom bar instead, within reach of a thumb. */
  primary?: ReactNode
  /** Filters, tabs or a search field belonging to this screen. */
  children?: ReactNode
}) {
  // `Page` puts the same node in a bottom bar below this width; only one of
  // the two ever renders it.
  const wide = useMinWidth('lg')

  return (
    <header className="mb-5 grid flex-none gap-4">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
        <div className="grid min-w-0 gap-1">
          {eyebrow && (
            <p className="text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
              {eyebrow}
            </p>
          )}
          {/* 22px: the page title labels where you are, and the subject of the
              screen — the patient's name — has to be able to be louder. */}
          <h1 className="text-2xl leading-tight font-bold tracking-tight">{title}</h1>
          {description && (
            <p className="max-w-prose text-sm text-fg-muted">{description}</p>
          )}
        </div>
        {(actions || (primary && wide)) && (
          <div className="flex flex-wrap items-center gap-3">
            {actions}
            {wide && primary}
          </div>
        )}
      </div>
      {children}
    </header>
  )
}
